import {
  Button,
  Card,
  HSpacer,
  Input,
  SegmentedButton,
  Text,
  VSpacer,
} from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { useSearch } from '@/hooks/useSearch';
import { getCustomerDashboardUrl } from '@/utilities/CustomerDashboardUrl';
import { ScheduledNotificationApi } from '@/utilities/api/ScheduledNotificationApi';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import MessageIcon from '@mui/icons-material/Message';
import PeopleIcon from '@mui/icons-material/People';
import Person from '@mui/icons-material/Person';
import { Autocomplete, CircularProgress, IconButton, Stack } from '@mui/material';
import { ScheduledNotificationStatus } from '@shared/enums/ScheduledNotificationStatus';
import { DeepLinkUrls } from '@shared/enums/ScheduledNotificationType';
import { FilterOptions } from '@shared/interfaces/ScheduledNotification';
import { formatMediumDate, getAccountName } from "@shared/utilities";
import { DateTime } from 'luxon';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQuery } from 'react-query';
import { formatFilterDisplay } from '../utils/filterFormatting';
import { BaseNotificationProps } from '../utils/NotificationTypes';
import { useFilterDisplayContext } from '../utils/useFilterDisplayContext';
import { NotificationRecipientsModal } from './NotificationRecipientsModal';
import { agronomyProgramsOnly } from '@/utilities/RewardsProgramUtility';

interface PreviewNotificationProps extends BaseNotificationProps {
  deepLink: DeepLinkUrls | null,
  isEditMode?: boolean,
  message: string,
  onCreateNotification: () => void,
  onEditSection: (section: 'message' | 'recipients' | 'schedule') => void,
  options?: FilterOptions,
  testID: string,
  title: string,
}

export const PreviewNotification: React.FC<PreviewNotificationProps> = ({
  deepLink,
  isEditMode = false,
  message,
  notificationId,
  onCreateNotification,
  onEditSection,
  options,
  recipientCount,
  csvRecipientData,
  retailerId,
  sendDate,
  status,
  testID,
  title,
}) => {
  const { t, i18n } = useTranslation();
  const [showRecipientsModal, setShowRecipientsModal] = useState<boolean>(false);
  const [contentTab, setContentTab] = useState<0 | 1>(0);
  const [selectedFarmer, setSelectedFarmer] = useState<{ id: string, name: string } | null>(null);

  const { setSearch: setRecipientSearch, debouncedSearch } = useSearch(1);

  const { filterDisplayContext, retailer: retailerData } = useFilterDisplayContext(retailerId);

  const getCurrentProgram = (): ApiRewardsProgram | null => {
    if (!retailerData?.rewardsPrograms) return null;

    const today = new Date();
    return agronomyProgramsOnly(retailerData.rewardsPrograms).find(program => {
      const startDate = new Date(program.startDate);
      const endDate = new Date(program.endDate);
      return startDate <= today && endDate >= today;
    }) || null;
  };

  const generateDeepLink = (linkType: DeepLinkUrls): string => {
    if (!retailerData?.shortUrlName) return linkType;

    const getSuffix = () => {
      switch (linkType) {
        case DeepLinkUrls.CustomerWallet:
          return '/wallet';
        case DeepLinkUrls.CustomerPromotions:
          return '/promotions';
        case DeepLinkUrls.CustomerCRD:
        default:
          return '';
      }
    };

    return getCustomerDashboardUrl(retailerData.shortUrlName, '[ACCESS_TOKEN]', getSuffix());
  };

  const replaceVariables = (text: string): string => {
    if (!retailerData) {
      return text;
    }

    const currentProgram = getCurrentProgram();
    let replacedText = text;

    replacedText = replacedText.replace(/\{\{retailer name\}\}/gi, retailerData.name);

    if (currentProgram) {
      replacedText = replacedText.replace(/\{\{campaign name\}\}/gi, currentProgram.name);
    }

    if (currentProgram) {
      const endDate = formatMediumDate(currentProgram.endDate, { language: 'en-US' });
      replacedText = replacedText.replace(/\{\{current program period end date\}\}/gi, endDate);

      const endDateFrench = formatMediumDate(
        currentProgram.endDate, { language: 'fr-CA' },
      );
      replacedText = replacedText.replace(
        /\{\{current program period end date \(french\)\}\}/gi,
        endDateFrench,
      );
    }
    return replacedText;
  };

  const hasFrenchVariables = !!message.match(/\{\{[^}]+ \(french\)\}\}/);
  const processedMessage = replaceVariables(message);
  const processedDeepLink = deepLink ? generateDeepLink(deepLink) : null;

  const isEditable = !status ||
    (status === ScheduledNotificationStatus.Scheduled ||
      status === ScheduledNotificationStatus.Draft);

  const {
    data: recipientsPages,
    fetchNextPage,
    hasNextPage,
    isFetching: isLoadingRecipients,
    isFetchingNextPage,
  } = useInfiniteQuery(
    [
      QueryKeys.LIST_RECIPIENTS,
      'preview',
      retailerId,
      debouncedSearch,
      options,
      csvRecipientData,
      notificationId,
    ],
    ({ pageParam }) => ScheduledNotificationApi.getRecipients({
      options: options ?? {},
      csvRecipientData: csvRecipientData ?? [],
      retailerId,
      scheduledNotificationId: notificationId,
      search: debouncedSearch || undefined,
      limit: 20,
      page: pageParam,
    }),
    {
      enabled: contentTab === 1,
      getNextPageParam: (lastPage) => (
        lastPage.lastPage > lastPage.page ? lastPage.page + 1 : undefined
      ),
    },
  );

  const recipientOptions = useMemo(
    () => recipientsPages?.pages?.flatMap((p) => p.data) ?? [],
    [recipientsPages],
  );

  const handleListboxScroll = useCallback((event: React.UIEvent<HTMLUListElement>) => {
    const listbox = event.currentTarget;
    const nearBottom = listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 50;
    if (nearBottom && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const previewBody = notificationId
    ? { scheduledNotificationId: notificationId, userId: selectedFarmer?.id ?? '' }
    : { retailerId, message, deepLink, options, title, userId: selectedFarmer?.id ?? '' };

  const { data: previewData, isFetching: isPreviewLoading } = useQuery(
    [
      QueryKeys.PREVIEW_NOTIFICATION_MESSAGE,
      notificationId,
      selectedFarmer?.id,
      retailerId,
      message,
      deepLink,
      title,
      options,
    ],
    () => ScheduledNotificationApi.previewMessage(previewBody),
    { enabled: !!selectedFarmer?.id },
  );

  const customerPreviewContent = !selectedFarmer ? (
    <Stack alignItems="center" direction="row" p="18px" spacing={1}>
      <Person sx={{ color: '#8a8a8a' }} />
      <Text category="body-medium" sx={{ color: '#8a8a8a' }}>
        {t($ => (
          $.notificationBuilder.preview.customerPreview.selectFarmerPlaceholder
        ))}
      </Text>
    </Stack>
  ) : (isPreviewLoading || !previewData) ? (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '80px' }}>
      <CircularProgress size={24} />
    </Stack>
  ) : (
    <Text
      category="body-large"
      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
    >
      {previewData?.message ?? ''}
    </Text>
  );

  return (
    <Stack alignItems="center" sx={{ width: '100%' }}>
      <Card sx={{ padding: '24px', width: '680px' }} testID={`${testID}-preview-card`}>
        <Text
          category="title-large"
          sx={{
            textAlign: 'center',
          }}
        >
          {t($ => $.notificationBuilder.preview.title)}
        </Text>
        <VSpacer size="8" />
        <Stack alignItems="center" direction="row">
          <CalendarMonthIcon />
          <HSpacer size="4" />
          <Text
            category="title-medium"
            sx={{
              flex: 1,
              fontSize: "18px",
              fontWeight: 500,
            }}
          >
            {!!sendDate && t($ => $.notificationBuilder.preview.scheduledFor, {
              date: new Intl.DateTimeFormat(
                i18n.language,
                { weekday: 'short', month: 'short', day: 'numeric' },
              ).format(sendDate),
              time: DateTime.fromJSDate(sendDate),
            })}
          </Text>
          {isEditable && (
            <IconButton
              aria-label={t($ => $.notificationBuilder.actions.editSchedule)}
              color="primary"
              data-testid={`${testID}-edit-schedule-button`}
              onClick={() => onEditSection('schedule')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        <VSpacer size="6" />
        <Stack>
          <Stack alignItems="center" direction="row">
            <PeopleIcon />
            <HSpacer size="4" />
            <Stack alignItems="center" direction="row" flex={1}>
              <Text
                category="title-medium"
                sx={{
                  fontSize: "18px",
                  fontWeight: 500,
                }}
              >
                {t(
                  $ => $.notificationBuilder.preview.recipientsCount,
                  { count: recipientCount ?? 0 },
                )}
              </Text>
              {!!recipientCount && (
                <>
                  <HSpacer size="2" />
                  <Button
                    color="primary"
                    onClick={() => setShowRecipientsModal(true)}
                    size="small"
                    sx={{ padding: 0, fontSize: "14px" }}
                    testID={`${testID}-view-recipients-button`}
                    variant="text"
                  >
                    {t($ => $.notificationBuilder.actions.viewRecipients).split(' ')[0]}
                  </Button>
                </>
              )}
            </Stack>
            {isEditable && (
              <IconButton
                aria-label={t($ => $.notificationBuilder.actions.editRecipients)}
                color='primary'
                data-testid={`${testID}-edit-recipients-button`}
                onClick={() => onEditSection('recipients')}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
          <Stack flex={1} sx={{ pl: '36px' }}>
            <VSpacer size="4" />
            <Stack
              sx={{
                backgroundColor: 'background.paper',
                borderRadius: '4px',
              }}
            >
              <Text
                category="body-small"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  fontWeight: 500,
                }}
              >
                {t($ => $.notificationBuilder.preview.selectedCriteria)}
              </Text>
              <VSpacer size="3" />
              {Object.keys(options || {}).length > 0 ? (
                <Stack spacing={1}>
                  {Object.entries(options || {}).map(([key, value]) => {
                    if ((Array.isArray(value) && value.length === 0) || value === null) {
                      return null;
                    }
                    const formattedText = formatFilterDisplay(
                      key as keyof FilterOptions, value, options || {}, filterDisplayContext,
                    );
                    return formattedText ? (
                      <Stack
                        alignItems="center"
                        direction="row"
                        key={key}
                        spacing={1}
                      >
                        <CheckIcon color="success" fontSize="small" />
                        <Text category="body-medium">
                          {formattedText}
                        </Text>
                      </Stack>
                    ) : null;
                  })}
                </Stack>
              ) : (
                <Stack alignItems="center" direction="row" spacing={1}>
                  <CheckIcon color="success" fontSize="small" />
                  <Text category="body-medium">
                    {(csvRecipientData || []).length > 0
                      ? t($ => $.notificationBuilder.preview.criteriaInfo.csv)
                      : recipientCount
                        ? t($ => $.notificationBuilder.preview.criteriaInfo.all)
                        : t($ => $.notificationBuilder.preview.criteriaInfo.none)
                    }
                  </Text>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
        <VSpacer size="6" />
        <Stack>
          <Stack alignItems="center" direction="row">
            <MessageIcon />
            <HSpacer size="4" />
            <Text
              category="title-medium"
              sx={{
                flex: 1,
                fontSize: "18px",
                fontWeight: 500,
              }}
            >
              {t($ => $.notificationBuilder.preview.contentTitle)}
            </Text>
            {isEditable && (
              <IconButton
                aria-label={t($ => $.notificationBuilder.preview.editNotification)}
                color='primary'
                data-testid={`${testID}-edit-message-button`}
                onClick={() => onEditSection('message')}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
          <Stack sx={{ pl: '36px' }}>
            <VSpacer size="4" />
            <SegmentedButton color="primary" selectedIndex={contentTab}>
              <Button
                onClick={() => setContentTab(0)}
                testID={`${testID}-preview-tab-button`}
              >
                {t($ => $.notificationBuilder.preview.contentToggle.preview)}
              </Button>
              <Button
                onClick={() => setContentTab(1)}
                testID={`${testID}-customer-specific-preview-tab-button`}
              >
                {t($ => $.notificationBuilder.preview.contentToggle.customerSpecificPreview)}
              </Button>
            </SegmentedButton>
            <VSpacer size="4" />
            {contentTab === 0 && (
              <Stack
                sx={{
                  backgroundColor: 'background.paper',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <Text
                  category="body-large"
                  sx={{ wordBreak: 'break-word' }}
                >
                  {title}
                </Text>
                <VSpacer size="4" />
                <Text
                  category="body-large"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {processedMessage}
                </Text>
                {!!processedDeepLink && (
                  <>
                    <VSpacer size="4" />
                    <Text
                      category="body-large"
                      sx={{ color: '#1976d2', wordBreak: 'break-all' }}
                    >
                      {processedDeepLink}
                    </Text>
                  </>
                )}
                {hasFrenchVariables && (
                  <>
                    <VSpacer size="4" />
                    <Text category="body-large">
                      {t($ => $.notificationBuilder.preview.unsubscribeText, { lng: 'fr-CA' })}
                    </Text>
                  </>
                )}
                <VSpacer size="4" />
                <Text category="body-large">
                  {t($ => $.notificationBuilder.preview.unsubscribeText)}
                </Text>
              </Stack>
            )}
            {contentTab === 1 && (
              <Stack spacing={2}>
                <Autocomplete
                  ListboxProps={{ onScroll: handleListboxScroll }}
                  filterOptions={(x) => x}
                  getOptionLabel={(option) => getAccountName(option.customer)}
                  isOptionEqualToValue={
                    (option, value) => option.customer.id === value.customer.id
                  }
                  loading={isLoadingRecipients}
                  onChange={(_, value) => {
                    setSelectedFarmer(
                      value
                        ? { id: value.customer.id, name: getAccountName(value.customer) }
                        : null,
                    );
                  }}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') {
                      setRecipientSearch(value);
                    }
                  }}
                  options={recipientOptions}
                  renderInput={(params) => (
                    <Input
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <Fragment>
                            {isLoadingRecipients && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        ),
                      }}
                      label={t($ => $.notificationBuilder.preview.customerPreview.searchLabel)}
                      testID={`${testID}-recipient-search`}
                    />
                  )}
                  value={selectedFarmer
                    ? recipientOptions.find((r) => r.customer.id === selectedFarmer.id) ?? null
                    : null
                  }
                />
                <Stack
                  sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '8px',
                    border: !selectedFarmer ? '1px dashed' : undefined,
                    borderColor: !selectedFarmer ? '#d6d6d6' : undefined,
                    overflow: 'hidden',
                  }}
                >
                  {customerPreviewContent}
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Card>
      <VSpacer size="7" />
      {!isEditMode && (
        <Stack alignItems="center" justifyContent="center">
          <Button
            color="primary"
            onClick={onCreateNotification}
            size="large"
            testID={`${testID}-create-button`}
            variant="contained"
          >
          {status === ScheduledNotificationStatus.Scheduled
            ? t($ => $.notificationBuilder.preview.updateButton)
            : t($ => $.notificationBuilder.preview.createButton)}
          </Button>
        </Stack>
      )}
      {showRecipientsModal && (
        <NotificationRecipientsModal
          csvRecipientsData={csvRecipientData || []}
          onClose={() => setShowRecipientsModal(false)}
          options={options || {} as FilterOptions}
          retailerId={retailerId}
          scheduledNotificationId={notificationId}
        />
      )}
    </Stack>
  );
};
